import React, { useEffect, useState } from "react";
import { Modal, Spin, message, Empty, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { FaSearch, FaEye } from "react-icons/fa";
import { GiMedicines, GiTestTubes } from "react-icons/gi";
import { HiOutlineShoppingCart } from "react-icons/hi";
import AuthService from "../../Services/AuthService";
import DrugServices from "../../Services/DrugServices";
import LabService from "../../Services/LabService";
import PharmaAndLabOrderService from "../../Services/PharmaAndLabOrderService";
import ConsentServices from "../../Services/ConsentServices";
import {
  createCartOrderDtlItem,
  createCartOrderHdrItem,
  getCurrentTime,
} from "../../Shared/CartHelper";
import { AddtoCart, getCartDetails } from "../../Redux/actions/cartActions";

const AFTER_BEFORE_FOOD_LABEL = {
    AF: "After Food",
    BF: "Before Food",
};

const getInitials = (name = "") =>
    name
        .replace("Dr.", "")
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const parsed = new Date(dateStr.replace(" ", "T"));
    if (Number.isNaN(parsed.getTime())) return dateStr;
    return parsed.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const isOrderAlreadySent = (status) => Boolean(status) && status !== "Decline" && status !== "";

function PrescriptionOrderModal({
  showModal,
  onClose,
  patientData,
  address,
}) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  
  const [cartLoader, setCartLoader] = useState(false);

  // Prescription image preview
  const [prescViewUrl, setPrescViewUrl] = useState(null);

  // Pharmacy picker
  const [pharmaModalOpen, setPharmaModalOpen] = useState(false);
  const [pharmacyList, setPharmacyList] = useState([]);
  const [pharmaPayload, setPharmaPayload] = useState({});
  const [pharmaSendLoader, setPharmaSendLoader] = useState(false);
  const [pharmaCheckLoader, setPharmaCheckLoader] = useState(false);

  // Lab picker
  const [labModalOpen, setLabModalOpen] = useState(false);
  const [labList, setLabList] = useState([]);
  const [labPayload, setLabPayload] = useState({});
  const [labSendLoader, setLabSendLoader] = useState(false);
  const [labCheckLoader, setLabCheckLoader] = useState(false);

  // Consent
  const [showConsent, setShowConsent] = useState(false);
  const [consentTarget, setConsentTarget] = useState(null);
  const [consentBtnDisable, setConsentBtnDisable] = useState(false);

  const dispatch = useDispatch();
  const { cartData } = useSelector((state) => state.cart);

  useEffect(() => {
    if (showModal && patientData?.patientId) {
      fetchPrescriptions();
    } else {
      setPrescriptions([]);
      setSelectedId(null);
      setSearch("");
    }
  }, [showModal, patientData]);

  const fetchPrescriptions = async () => {
    setLoading(true);
    const payload = {
      patientId: patientData.patientId,
      detailsYN: "Y",
      status: 2,
      pageNo: 1,
      pageSize: 10,
    };
    try {
      const response = await AuthService.getMyPrescriptionView(payload);
      if (response?.data?.length > 0) {
        setPrescriptions(response.data);
        setSelectedId(response.data[0].consultationId);
      } else {
        setPrescriptions([]);
        setSelectedId(null);
      }
    } catch (e) {
      console.error(e);
      setPrescriptions([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  };

  const filteredConsultations = prescriptions.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
        c.userName?.toLowerCase().includes(q) ||
        formatDate(c.consultationDate)?.toLowerCase().includes(q) ||
        String(c.consultationId)?.toLowerCase().includes(q)
    );
  });

  const selectedConsultation = prescriptions.find((c) => c.consultationId === selectedId);
  const allMedicines = selectedConsultation?.drugPrescription || [];
  const allLabTests = selectedConsultation?.labPrescription || [];

  const cartableMedicines = allMedicines.filter((m) => m.isAvailableForCart === "Y");
  const nonCartableMedicines = allMedicines.filter((m) => m.isAvailableForCart !== "Y");
  const cartableLabTests = allLabTests.filter((l) => l.isAvailableForCart === "Y");
  const nonCartableLabTests = allLabTests.filter((l) => l.isAvailableForCart !== "Y");

  const resolveDrugCatalogItem = async (drugItem) => {
      const payload = { status:1, pageNo: 1, pageSize: 40 };
      if (drugItem.drugName) payload.medicineName = drugItem.drugName;
      if (drugItem.drugCode) payload.drugId = drugItem.drugCode;
      try {
          const response = await DrugServices.prescOrdDrugMdm(payload);
          const list = response?.data?.data || response?.data || [];
          return list.find((d) => d.code === drugItem.drugCode) || list[0] || null;
      } catch (e) {
          console.log(e);
          return null;
      }
  };

  const resolveLabCatalogItem = async (labItem) => {
      const payload = { status:1 };
      if(labItem.labTestName) payload.testName = labItem.labTestName;
      try {
          const response = await LabService.LabTestMdm(payload);
          const list = response?.data?.data || response?.data || [];
          return list.find((l) => l.labTestId === labItem.labTestId) || list[0] || null;
      } catch (e) {
          console.log(e);
          return null;
      }
  };

  const buildOrUpdateHdr = (existingHdrs, type, dtlPayloads, vendorAccBranch, consultationId, prescriptionName) => {
      const now = getCurrentTime();
      let hdrs = [...existingHdrs];
      const hdrIndex = hdrs.findIndex((h) => h.orderType === type);

      if (hdrIndex === -1) {
          const [firstDtl, ...restDtl] = dtlPayloads;
          const newHdr = createCartOrderHdrItem({
              Acc_branch: vendorAccBranch,
              dtlItem: firstDtl,
              account: address || null,
              user: patientData,
              patientId: patientData.patientId,
              type,
          });
          newHdr.cartOrderDtl = [firstDtl, ...restDtl];
          newHdr.amount = newHdr.cartOrderDtl.reduce((sum, i) => sum + Number(i.amount), 0);
          newHdr.paidAmount = newHdr.cartOrderDtl.reduce((sum, i) => sum + Number(i.paidAmount), 0);
          newHdr.consultationId = consultationId;
          newHdr.prescriptionName = prescriptionName;
          hdrs.push(newHdr);
          return hdrs;
      }

      const hdr = { ...hdrs[hdrIndex] };
      let updatedDtl = [...hdr.cartOrderDtl];

      dtlPayloads.forEach((newItem) => {
          const existingIdx = updatedDtl.findIndex((i) => i.orderCode === newItem.orderCode);
          if (existingIdx !== -1) {
              const existing = updatedDtl[existingIdx];
              const qty = existing.quantity + 1;
              updatedDtl[existingIdx] = {
                  ...existing,
                  quantity: qty,
                  amount: qty * existing.unitPrice,
                  paidAmount: qty * existing.unitPrice,
                  orderState: "U",
                  modifiedDate: now,
              };
          } else {
              updatedDtl.push(newItem);
          }
      });

      hdr.cartOrderDtl = updatedDtl;
      hdr.amount = updatedDtl.reduce((sum, i) => sum + Number(i.amount), 0);
      hdr.paidAmount = updatedDtl.reduce((sum, i) => sum + Number(i.paidAmount), 0);
      hdr.modifiedDate = now;
      hdr.consultationId = consultationId;
      hdr.prescriptionName = prescriptionName;

      hdrs[hdrIndex] = hdr;
      return hdrs;
  };

  const handleAddToCart = async () => {
      if (!patientData) {
          message.error("Please select a patient");
          return;
      }
      if (!selectedConsultation) {
          message.info("Please select a prescription first");
          return;
      }
      if (cartableMedicines.length === 0 && cartableLabTests.length === 0) {
          message.info("No items in this prescription are available to add to cart");
          return;
      }

      setCartLoader(true);
      try {
          const resolvedDrugs = await Promise.all(cartableMedicines.map(resolveDrugCatalogItem));
          const resolvedLabs = await Promise.all(cartableLabTests.map(resolveLabCatalogItem));

          const drugDtlPayloads = [];
          let unresolvedDrugNames = [];
          cartableMedicines.forEach((rxItem, idx) => {
              const catalogItem = resolvedDrugs[idx];
              if (!catalogItem) {
                  unresolvedDrugNames.push(rxItem.drugName);
                  return;
              }
              drugDtlPayloads.push({
                  dtl: createCartOrderDtlItem({
                      item: {
                          code: catalogItem.code || rxItem.drugCode,
                          name: catalogItem.medicineName || rxItem.drugName,
                          price: catalogItem.mrp || 0,
                          vendorId: catalogItem.pharmacyId || "DZA4401049",
                          drugCategory: catalogItem.drugCategory || "drugs",
                      },
                      type: "DRUG",
                      patientId: patientData.patientId,
                      user: patientData,
                      cartId: cartData?.cartId,
                  }),
                  vendor: { acc_Id: catalogItem.pharmacyId || "DZA4401049", bran_Id: catalogItem.branchId || "DZC6771062" },
              });
          });

          const labDtlPayloads = [];
          let unresolvedLabNames = [];
          cartableLabTests.forEach((rxItem, idx) => {
              const catalogItem = resolvedLabs[idx];
              if (!catalogItem) {
                  unresolvedLabNames.push(rxItem.labTestName);
                  return;
              }
              labDtlPayloads.push({
                  dtl: createCartOrderDtlItem({
                      item: {
                          code: catalogItem.testCode || rxItem.labTestId,
                          name: catalogItem.testName || rxItem.labTestName,
                          price: catalogItem.listPrice || 0,
                          vendorId: catalogItem.labCode || "DZA4401049",
                          labCategory: catalogItem.testType || "lab",
                      },
                      type: "LAB",
                      patientId: patientData.patientId,
                      user: patientData,
                      cartId: cartData?.cartId,
                  }),
                  vendor: { acc_Id: catalogItem.labCode || "DZA4401049", bran_Id: catalogItem.branchId || "DZC6771062" },
              });
          });

          if (drugDtlPayloads.length === 0 && labDtlPayloads.length === 0) {
              message.error("Could not resolve pricing/vendor for any item in this prescription");
              setCartLoader(false);
              return;
          }
          if (unresolvedDrugNames.length > 0 || unresolvedLabNames.length > 0) {
              message.warning(
                  `Skipped (not found in catalog): ${[...unresolvedDrugNames, ...unresolvedLabNames].join(", ")}`,
              );
          }

          let freshCart = cartData;
          const existingHdrs = freshCart?.cartOrderHdr ? [...freshCart.cartOrderHdr] : [];
          let updatedHdrs = existingHdrs;
          const consultationId = selectedConsultation.consultationId;
          const prescriptionName = selectedConsultation.imageName;

          if (drugDtlPayloads.length > 0) {
              updatedHdrs = buildOrUpdateHdr(
                  updatedHdrs,
                  "DRUG",
                  drugDtlPayloads.map((p) => p.dtl),
                  drugDtlPayloads[0].vendor,
                  consultationId,
                  prescriptionName,
              );
          }
          if (labDtlPayloads.length > 0) {
              updatedHdrs = buildOrUpdateHdr(
                  updatedHdrs,
                  "LAB",
                  labDtlPayloads.map((p) => p.dtl),
                  labDtlPayloads[0].vendor,
                  consultationId,
                  prescriptionName,
              );
              updatedHdrs = updatedHdrs.map((item => {
                  if(item.orderType === "LAB"){
                      item.labCategory = 'External Lab Test';
                  }
                  return item;
              }))
          }

          const grandTotalAmount = updatedHdrs.reduce((sum, hdr) => sum + (hdr?.paidAmount || 0), 0);

          const payload = {
              ...(freshCart || {}),
              cartId: freshCart?.cartId,
              cartOrderHdr: updatedHdrs,
              cartType: freshCart?.cartType ?? 1,
              patientId: patientData.patientId,
              paymentType: freshCart?.paymentType ?? "",
              status: 1,
              modifiedDate: getCurrentTime(),
              totalAmount: grandTotalAmount,
          };

          await dispatch(AddtoCart(payload));
          await dispatch(getCartDetails({ patientId: patientData.patientId }));
          message.success("Prescription items added to cart successfully.");
      } catch (error) {
          console.log(error);
          message.error("Unable to add items to cart");
      } finally {
          setCartLoader(false);
      }
  };

  // Pharmacy Send Flow
  const handleOpenPharmacyPicker = async () => {
      if (nonCartableMedicines.length === 0 || !selectedConsultation) return;

      if (selectedConsultation.shareEpresWithPharmacy === "N") {
          setConsentTarget("pharmacy");
          setShowConsent(true);
          return;
      }

      if (isOrderAlreadySent(selectedConsultation.drugPrescriptionStatus)) {
          message.error("Medicine Order is Already Placed");
          return;
      }

      setPharmaCheckLoader(true);
      try {
          const medListPayload = { consultationId: selectedConsultation.consultationId, status: 1 };
          const drugList = await PharmaAndLabOrderService.GetDispensedDrugsDtl(medListPayload);

          if (Array.isArray(drugList?.data) && drugList.data.length === 0) {
              message.error("Unable to place order, Medicine List is empty");
              return;
          }

          const pharmaListPayload = { status: 1 };
          if (selectedConsultation.userId) pharmaListPayload.userId = selectedConsultation.userId;
          const response = await PharmaAndLabOrderService.GetPharmacyList(pharmaListPayload);
          setPharmacyList(response.data);
          setPharmaModalOpen(true);
      } catch (e) {
          console.log(e);
          message.error("Unable to fetch pharmacies");
      } finally {
          setPharmaCheckLoader(false);
      }
  };

  const handleSelectedPharma = (selectedItem) => {
      const item = pharmacyList.find((p) => p.id === selectedItem);
      if (!item) {
          message.error("Something went wrong please try after sometime!!");
          return;
      }
      setPharmaPayload({
          ...item,
          pharmacyId: item.pharmacyId,
          pharmacyName: item.pharmacyName,
          pharmacyMailId: item.pharmacyMailId,
          pharmacyMobile: item.pharmacyMobile,
      });
  };

  const handleSendToPharmacy = async () => {
      if (!pharmaPayload?.pharmacyId) {
          message.error("Please select a pharmacy");
          return;
      }
      setPharmaSendLoader(true);
      const linkPayload = {
          consultationId: selectedConsultation.consultationId,
          patientId: patientData.patientId,
          userId: patientData.userId,
          appointmentId: selectedConsultation.appointmentId,
          prescriptionShareType: "p",
          vendorId: pharmaPayload.pharmacyId,
          vendorName: pharmaPayload.pharmacyName,
          vendorMailId: pharmaPayload.pharmacyMailId,
          vendorMobile: pharmaPayload.pharmacyMobile,
          drugPrescriptionStatus: "Sent",
          rejectedPharmacyRemarks: null,
          sourceChannel: "PATIENT",
          orderSource: "PRESCRIPTION"
      };

      try {
          const response = await PharmaAndLabOrderService.SharePharmaLabAPI(linkPayload);
          if (response.data.message !== "Prescription sent successfully") {
              message.error("Medicine order is already placed");
              return;
          }
          message.success(`Medicine order sent to ${linkPayload.vendorName}`);
          setPharmaModalOpen(false);
          fetchPrescriptions();
      } catch (error) {
          message.error(error?.response?.data?.message || "Something went wrong please try after sometime!!");
      } finally {
          setPharmaSendLoader(false);
      }
  };

  // Lab Send Flow
  const handleOpenLabPicker = async () => {
      if (nonCartableLabTests.length === 0 || !selectedConsultation) return;

      if (selectedConsultation.shareEpresWithDiagnostics === "N") {
          setConsentTarget("lab");
          setShowConsent(true);
          return;
      }

      if (isOrderAlreadySent(selectedConsultation.labPrescriptionStatus)) {
          message.error("Lab Order Already Placed");
          return;
      }

      setLabCheckLoader(true);
      try {
          const labListPayload = { consultationId: selectedConsultation.consultationId, status: 1 };
          const labOrderList = await PharmaAndLabOrderService.GetLabTestOrderDtl(labListPayload);

          if (Array.isArray(labOrderList?.data) && labOrderList.data.length === 0) {
              message.error("Unable to place order, Lab List is empty");
              return;
          }

          const labVendorListPayload = { status: 1 };
          if (selectedConsultation.userId) labVendorListPayload.userId = selectedConsultation.userId;
          const response = await PharmaAndLabOrderService.GetLabList(labVendorListPayload);
          setLabList(response.data);
          setLabModalOpen(true);
      } catch (e) {
          console.log(e);
          message.error("Unable to fetch labs");
      } finally {
          setLabCheckLoader(false);
      }
  };

  const handleLabSelect = (selectedItem) => {
      const item = labList.find((l) => l.id === selectedItem);
      if (!item) {
          message.error("Something went wrong please try after sometime!!");
          return;
      }
      setLabPayload({
          ...item,
          labId: item.labId,
          labName: item.labName,
          labMailId: item.labMailId,
          labMobile: item.labMobile,
      });
  };

  const handleSendToLab = async () => {
      if (!labPayload?.labId) {
          message.error("Please select a lab");
          return;
      }
      setLabSendLoader(true);
      const linkPayload = {
          consultationId: selectedConsultation.consultationId,
          patientId: patientData.patientId,
          userId: patientData.userId,
          appointmentId: selectedConsultation.appointmentId,
          prescriptionShareType: "l",
          vendorId: labPayload.labId,
          vendorName: labPayload.labName,
          vendorMailId: labPayload.labMailId,
          vendorMobile: labPayload.labMobile,
          labPrescriptionStatus: "Sent",
          rejectedLabRemarks: null,
          sourceChannel: "PATIENT",
          orderSource: "PRESCRIPTION"
      };

      try {
          const response = await PharmaAndLabOrderService.SharePharmaLabAPI(linkPayload);
          if (response.data.message !== "Prescription sent successfully") {
              message.error("Lab order is already placed");
              return;
          }
          message.success(`Lab order sent to ${linkPayload.vendorName}`);
          setLabModalOpen(false);
          fetchPrescriptions();
      } catch (error) {
          message.error(error?.response?.data?.message || "Something went wrong please try after sometime!!");
      } finally {
          setLabSendLoader(false);
      }
  };

  const handleConsModCancel = () => {
      setShowConsent(false);
      setConsentTarget(null);
  };

  const sendConsentRequest = async () => {
      if (!selectedConsultation) return;
      setConsentBtnDisable(true);
      const payload = { appointmentId: selectedConsultation.appointmentId };
      try {
          if (ConsentServices.GetPatientConsent) {
             await ConsentServices.GetPatientConsent(payload);
          } else {
             await ConsentServices.consentpost(payload); 
          }
          message.success("Request is Send to the patient side");
      } catch (error) {
          message.error("An error occured");
      } finally {
          setShowConsent(false);
          setConsentTarget(null);
          setConsentBtnDisable(false);
      }
  };

  return (
    <Modal
      open={showModal}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={700}
      closeIcon={false}
      className="p-0 overflow-hidden"
    >
      <div className="font-sans px-2 pb-2">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 mt-2">
            <h2 className="text-xl font-bold text-[#189FAA]">Your Prescription</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>

        {/* Search */}
        <div className="mt-4">
            <label className="block text-sm font-semibold text-[#232323] mb-1">
                Search Prescription
            </label>
            <div className="relative">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by doctor name, date or ID..."
                    className="w-full border border-gray-300 rounded-md pl-3 pr-9 py-2 text-sm outline-none placeholder:text-xs placeholder:text-gray-400 focus:border-[#189FAA]"
                />
                <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            </div>
        </div>

        {/* Prescription list */}
        <div className="mt-3 max-h-56 overflow-y-auto scrollWidth border border-gray-200 rounded-md">
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <Spin size="small" tip="Loading" />
                </div>
            ) : filteredConsultations.length === 0 ? (
                <div className="py-6">
                    <Empty description="No prescriptions found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            ) : (
                filteredConsultations.map((c, idx) => {
                    const isSelected = c.consultationId === selectedId;
                    return (
                        <div
                            key={c.consultationId}
                            onClick={() => setSelectedId(c.consultationId)}
                            className={`flex items-center justify-between gap-3 px-3 py-3 cursor-pointer transition-colors ${
                                idx !== filteredConsultations.length - 1 ? "border-b border-gray-200" : ""
                            } ${isSelected ? "bg-[#e6f7f8]" : "hover:bg-gray-50"}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#189FAA] text-white flex items-center justify-center text-xs font-bold">
                                    {getInitials(c.userName)}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-[#232323] truncate">
                                        {c.userName}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {formatDate(c.consultationDate)} &bull; Consultation ID: {c.consultationId}
                                    </p>
                                </div>
                            </div>
                            {c.imageName && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPrescViewUrl(`${process.env.REACT_APP_IMAGE_URL}${c.imageName}`);
                                    }}
                                    className="flex-shrink-0 w-8 h-8 rounded-md border border-[#189FAA] text-[#189FAA] flex items-center justify-center hover:bg-[#189FAA] hover:text-white transition-colors"
                                    aria-label="View prescription image"
                                >
                                    <FaEye size={13} />
                                </button>
                            )}
                        </div>
                    );
                })
            )}
        </div>

        {/* Medicine and Lab Test */}
        {selectedConsultation && (
            <div className="mt-5 border-t border-gray-100 pt-3">
                <h3 className="text-base font-bold text-[#189FAA]">Medicine and Lab Test</h3>

                {/* Medicine */}
                {allMedicines.length > 0 && (
                    <>
                        <div className="flex items-center justify-between mt-2">
                            <p className="text-sm font-semibold text-[#232323]">Medicine</p>
                            {nonCartableMedicines.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleOpenPharmacyPicker}
                                    disabled={pharmaCheckLoader}
                                    className="text-[11px] font-semibold text-[#189FAA] underline disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {pharmaCheckLoader ? "Checking..." : `Send to Pharmacy (${nonCartableMedicines.length})`}
                                </button>
                            )}
                        </div>
                        <div className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-200">
                            {allMedicines.map((med) => {
                                const unavailable = med.isAvailableForCart !== "Y";
                                return (
                                    <div
                                        key={med.id}
                                        className={`flex items-center justify-between gap-3 px-3 py-3 ${
                                            unavailable ? "opacity-60 bg-gray-50" : ""
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex-shrink-0 w-9 h-9 rounded-md bg-[#fdeef0] flex items-center justify-center">
                                                <GiMedicines className="text-[#c14a5c]" size={18} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-[#232323] truncate">
                                                    {med.drugName}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {med.composition || "Tablet"}
                                                    {med.duration ? ` \u2022 ${med.duration} day(s)` : ""}
                                                </p>
                                                {unavailable && (
                                                    <p className="text-[10px] text-orange-500 mt-0.5">
                                                        Send to pharmacy — not directly orderable
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 flex items-center gap-3">
                                            <div className="text-right">
                                              <p className="text-sm font-semibold text-[#189FAA]">{med.dosage}</p>
                                              <p className="text-[10px] text-gray-400">
                                                  {AFTER_BEFORE_FOOD_LABEL[med.afterBeforeFood] || "-"}
                                              </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Lab Test */}
                {allLabTests.length > 0 && (
                    <>
                        <div className="flex items-center justify-between mt-4">
                            <p className="text-sm font-semibold text-[#232323]">Lab Test</p>
                            {nonCartableLabTests.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleOpenLabPicker}
                                    disabled={labCheckLoader}
                                    className="text-[11px] font-semibold text-[#189FAA] underline disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {labCheckLoader ? "Checking..." : `Send to Lab (${nonCartableLabTests.length})`}
                                </button>
                            )}
                        </div>
                        <div className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-200">
                            {allLabTests.map((test) => {
                                const unavailable = test.isAvailableForCart !== "Y";
                                return (
                                    <div
                                        key={test.id}
                                        className={`flex items-center justify-between gap-3 px-3 py-3 ${
                                            unavailable ? "opacity-60 bg-gray-50" : ""
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex-shrink-0 w-9 h-9 rounded-md bg-[#e6f7f8] flex items-center justify-center">
                                                <GiTestTubes className="text-[#189FAA]" size={18} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-[#232323] truncate">
                                                    {test.labTestName}
                                                </p>
                                                <p className="text-xs text-gray-500">Lab Test</p>
                                                {unavailable && (
                                                    <p className="text-[10px] text-orange-500 mt-0.5">
                                                        Send to lab — not directly orderable
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {allMedicines.length === 0 && allLabTests.length === 0 && (
                    <div className="py-6">
                        <Empty description="No medicine or lab test in this prescription" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                )}
            </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-4 mt-6">
            <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 border border-[#189FAA] font-sans text-sm font-medium text-[#189FAA] rounded-md hover:bg-[#f0fbfc] transition-colors"
            >
                Cancel
            </button>
            <button
                type="button"
                onClick={handleAddToCart}
                disabled={
                    !selectedConsultation ||
                    cartLoader ||
                    (cartableMedicines.length === 0 && cartableLabTests.length === 0)
                }
                className="flex items-center gap-2 px-5 py-2 bg-[#189FAA] font-sans text-sm font-semibold text-white rounded-md hover:bg-[#157e87] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <HiOutlineShoppingCart size={16} />
                {cartLoader ? "Adding..." : "Add To Cart"}
            </button>
        </div>

        {/* Modals for Send to Pharmacy / Lab / Consent */}
        <Modal
            title={<span className="text-sm font-semibold font-sans text-[#189FAA]">Medicine Order</span>}
            open={pharmaModalOpen}
            onCancel={() => setPharmaModalOpen(false)}
            footer={[]}
            destroyOnClose
        >
            <div className="flex gap-2">
                <Select className="w-[80%]" placeholder="Select pharmacy" onChange={handleSelectedPharma}>
                    {pharmacyList?.map((p) => (
                        <Select.Option key={p.id} value={p.id}>
                            {p.pharmacyName}
                        </Select.Option>
                    ))}
                </Select>
                <button
                    onClick={handleSendToPharmacy}
                    disabled={pharmaSendLoader}
                    className="p-1 px-1 rounded-md bg-[#189FAA] text-white text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Send Order
                </button>
            </div>
        </Modal>

        <Modal
            title={<span className="text-sm font-semibold font-sans text-[#189FAA]">Lab Order</span>}
            open={labModalOpen}
            onCancel={() => setLabModalOpen(false)}
            footer={[]}
            destroyOnClose
        >
            <div className="flex gap-2">
                <Select className="w-[80%]" placeholder="Select lab" onChange={handleLabSelect}>
                    {labList?.map((l) => (
                        <Select.Option key={l.id} value={l.id}>
                            {l.labName}
                        </Select.Option>
                    ))}
                </Select>
                <button
                    onClick={handleSendToLab}
                    disabled={labSendLoader}
                    className="p-1 px-1 rounded-md bg-[#189FAA] text-white text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Send Order
                </button>
            </div>
        </Modal>

        <Modal
            title={<span className="text-sm font-semibold font-sans text-[#189FAA]">Get Consent from patient</span>}
            open={showConsent}
            onCancel={handleConsModCancel}
            footer={[]}
            destroyOnClose
        >
            <div className="flex flex-col gap-2">
                <div className="text-sm font-sans text-[#232323]">
                    This patient didn't give consent to share E-prescription with the{" "}
                    {consentTarget === "lab" ? "lab" : "pharmacy"}, to get consent from the patient send a request.
                </div>
                <button
                    onClick={sendConsentRequest}
                    className="p-1 rounded-md bg-[#189FAA] text-white text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={consentBtnDisable}
                >
                    Send Request
                </button>
            </div>
        </Modal>

        {/* Prescription Image Preview Modal — rendered above the main modal */}
        <Modal
            open={!!prescViewUrl}
            onCancel={() => setPrescViewUrl(null)}
            footer={null}
            destroyOnClose
            width="80%"
            style={{ top: 20, zIndex: 1100 }}
            title={<span className="text-sm font-semibold font-sans text-[#189FAA]">Prescription Preview</span>}
        >
            {prescViewUrl && (
                <iframe
                    src={prescViewUrl}
                    title="Prescription"
                    className="w-full border-0 rounded-md"
                    style={{ height: "75vh" }}
                />
            )}
        </Modal>

      </div>
    </Modal>
  );
}

export default PrescriptionOrderModal;
